import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Chess, type Move, type Square } from "chess.js";

import { ApiError } from "@/pages/auth/api";
import { resolveModelRuntimeErrorMessage } from "@/blocks/error/ModelRuntimeErrorBlock";
import { GameEndModal } from "@/blocks/GameEndModal";
import { ResignConfirm } from "@/blocks/ResignConfirm";
import { resolveGameEnd } from "@/blocks/game-end/resolveGameEnd";
import type { GameEndViewModel } from "@/blocks/game-end/types";
import { LoadingBlock } from "@/blocks/LoadingBlock";
import {
  getStoredSessionToken,
  getStoredUsername,
} from "@/pages/auth/storage";
import { AnalysisModelChoicePanel } from "@/pages/analysis/AnalysisModelChoicePanel";
import { StockfishAnalysisPanel } from "@/pages/analysis/StockfishAnalysisPanel";
import { formatStockfishScore } from "@/pages/analysis/engine/stockfishProtocol";
import type { StockfishPv } from "@/pages/analysis/engine/stockfishTypes";
import { formatStockfishPvSan } from "@/pages/analysis/engine/stockfishView";
import { useStockfishEvaluation } from "@/pages/analysis/engine/useStockfishEvaluation";
import {
  createPlayGame,
  getActivePlayGame,
  getPlayGamePgn,
  getPlayGame,
  getPublicPlayGame,
  resignPlayGame,
  submitPlayMove,
  syncPublicPlayGame,
  syncPlayGame,
  type PlayGameSnapshot,
} from "@/pages/play/api";
import { temperatureToHardness } from "@/pages/play/challengeDifficulty";
import { catie3ChallengeCards, challengeCards } from "@/pages/play/catalog";
import { lichessBoardPalette } from "@/shared/config/siteContent";
import { replayUciMove, resolveUciMove } from "@/shared/ui/chessMove";
import { ChessSurface } from "@/shared/ui/ChessSurface";
import { normalizeReviewPly } from "@/shared/ui/reviewPly";

type OpponentPresentation = {
  title: string;
  subtitle: string;
};

function resolveVisibleError(error: unknown, fallback: string) {
  const detail = error instanceof Error ? error.message : fallback;
  return resolveModelRuntimeErrorMessage(error, detail);
}

type SessionAccessMode = "player" | "public";
type SessionRoomMode = "player" | "spectator" | "replay";

const opponentPresentationById = new Map<string, OpponentPresentation>();

for (const card of [...challengeCards, ...catie3ChallengeCards]) {
  if (card.modelId) {
    opponentPresentationById.set(card.modelId, {
      title: card.title,
      subtitle: card.subtitle,
    });
  }
  for (const variant of card.variants ?? []) {
    opponentPresentationById.set(variant.modelId, {
      title: card.title,
      subtitle: variant.label,
    });
  }
}

function buildBoard(snapshot: PlayGameSnapshot, moveCount = snapshot.moves.length) {
  const board = snapshot.initial_fen ? new Chess(snapshot.initial_fen) : new Chess();
  const replayCount = Math.max(0, Math.min(moveCount, snapshot.moves.length));
  for (const move of snapshot.moves.slice(0, replayCount)) {
    replayUciMove(board, move);
  }
  return board;
}

function moveToUci(move: Move) {
  return `${move.from}${move.to}${move.promotion ?? ""}`;
}

function formatClock(ms: number) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

function parseServerTimestamp(value: string) {
  if (/[zZ]$|[+-]\d{2}:\d{2}$/.test(value)) {
    return Date.parse(value);
  }
  return Date.parse(`${value}Z`);
}

function deriveLiveClocks(snapshot: PlayGameSnapshot, nowMs: number) {
  let userClockMs = snapshot.user_clock_ms;
  let modelClockMs = snapshot.model_clock_ms;

  if (snapshot.result) {
    return { userClockMs, modelClockMs };
  }

  const startedAtMs = parseServerTimestamp(snapshot.last_move_started_at);
  if (Number.isNaN(startedAtMs)) {
    return { userClockMs, modelClockMs };
  }

  const elapsedMs = Math.max(0, nowMs - startedAtMs);
  if (snapshot.state === "USER_TURN" && snapshot.active_side === "user") {
    userClockMs = Math.max(0, userClockMs - elapsedMs);
  } else if (
    snapshot.state === "MODEL_THINKING" &&
    snapshot.active_side === "model"
  ) {
    modelClockMs = Math.max(0, modelClockMs - elapsedMs);
  }

  return { userClockMs, modelClockMs };
}

function resolveOpponentPresentation(modelId: string): OpponentPresentation {
  return (
    opponentPresentationById.get(modelId) ?? {
      title: modelId.replace(/\.(best|final)$/i, ""),
      subtitle: "Model challenger",
    }
  );
}

function formatTimeControlLabel(snapshot: PlayGameSnapshot) {
  return `${snapshot.time_control.minutes}+${snapshot.time_control.increment_seconds}`;
}

const reviewDateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

function formatOptionalReviewDate(value?: string | null) {
  if (!value) {
    return null;
  }

  const timestamp = parseServerTimestamp(value);
  if (!Number.isFinite(timestamp)) {
    return null;
  }

  return reviewDateFormatter.format(new Date(timestamp));
}

function isKeyboardNavigationBlocked(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return (
    target.isContentEditable ||
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.tagName === "SELECT" ||
    target.tagName === "BUTTON" ||
    target.tagName === "A"
  );
}

function resolveSessionStatus(
  snapshot: PlayGameSnapshot,
  opponentTitle: string,
  interactionLocked: boolean,
  hasError: boolean,
  roomMode: SessionRoomMode,
) {
  if (roomMode === "replay") {
    return {
      tone: "settled",
      label: "Replay archive",
      detail: "This link is bound to the full game record.",
      note: null,
    };
  }

  if (roomMode === "spectator") {
    if (snapshot.state === "USER_TURN") {
      return {
        tone: "spectator",
        label: "Player to move",
        detail: "Spectating live. The board updates automatically.",
        note: hasError ? "Live feed is retrying." : null,
      };
    }

    if (snapshot.state === "MODEL_DELAYING") {
      return {
        tone: "incoming",
        label: "Move incoming",
        detail: "Model reply confirmed.",
        note: hasError ? "Live feed is retrying." : null,
      };
    }

    return {
      tone: "thinking",
      label: `${opponentTitle} is thinking`,
      detail: "Spectating live. The board updates automatically.",
      note: hasError ? "Live feed is retrying." : null,
    };
  }

  if (snapshot.result) {
    return {
      tone: "settled",
      label: "Match complete",
      detail: "Result sealed. Review the finish or return to the floor.",
      note: null,
    };
  }

  if (interactionLocked) {
    return {
      tone: "warning",
      label: "Clock expired",
      detail: "Finalizing the timeout result for this room.",
      note: hasError ? "Room sync is retrying." : null,
    };
  }

  if (snapshot.state === "USER_TURN") {
    return {
      tone: "active",
      label: "Your move",
      detail: null,
      note: hasError ? "Connection hiccup. Room authority is retrying." : null,
    };
  }

  if (snapshot.state === "MODEL_DELAYING") {
    return {
      tone: "incoming",
      label: "Move incoming",
      detail: "Reply confirmed.",
      note: hasError ? "Connection hiccup. Room authority is retrying." : null,
    };
  }

  return {
    tone: "thinking",
    label: `${opponentTitle} is thinking`,
    detail: null,
    note: hasError ? "Connection hiccup. Room authority is retrying." : null,
  };
}

export function PlaySessionPage() {
  const navigate = useNavigate();
  const { gameId = "" } = useParams();
  const boardShellRef = useRef<HTMLDivElement | null>(null);
  const moveLogRef = useRef<HTMLDivElement | null>(null);
  const dragSourceSquareRef = useRef<Square | null>(null);
  const syncInFlightRef = useRef(false);
  const timeoutSyncKeyRef = useRef<string | null>(null);
  const [snapshot, setSnapshot] = useState<PlayGameSnapshot | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [accessMode, setAccessMode] = useState<SessionAccessMode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedSquare, setSelectedSquare] = useState<Square | null>(null);
  const [legalTargets, setLegalTargets] = useState<Square[]>([]);
  const [boardWidth, setBoardWidth] = useState(560);
  const [clockNowMs, setClockNowMs] = useState(() => Date.now());
  const [copyState, setCopyState] = useState<"idle" | "copying" | "copied" | "error">("idle");
  const [copyError, setCopyError] = useState<string | null>(null);
  const [isResignConfirmOpen, setIsResignConfirmOpen] = useState(false);
  const [isResigning, setIsResigning] = useState(false);
  const [resignError, setResignError] = useState<string | null>(null);
  const [rematchState, setRematchState] = useState<"idle" | "starting" | "error">("idle");
  const [rematchError, setRematchError] = useState<string | null>(null);
  const [gameEndModal, setGameEndModal] = useState<GameEndViewModel | null>(null);
  const [gameEndVersion, setGameEndVersion] = useState<number | null>(null);
  const [reviewPly, setReviewPly] = useState<number | null>(null);
  const [expandedPv, setExpandedPv] = useState<number | null>(null);
  const [showAllEngineLines, setShowAllEngineLines] = useState(false);

  useEffect(() => {
    const sessionToken = getStoredSessionToken();

    let cancelled = false;
    const applyLoadedSnapshot = (
      next: PlayGameSnapshot,
      nextAccessMode: SessionAccessMode,
    ) => {
      if (cancelled) {
        return;
      }
      setSnapshot(next);
      setAccessMode(nextAccessMode);
      setStatus("ready");
      setError(null);
      if (next.result) {
        setGameEndVersion(next.version);
      }
    };

    const loadPublic = async () => {
      const next = await getPublicPlayGame(gameId);
      applyLoadedSnapshot(next, "public");
    };

    const load = async () => {
      try {
        if (!sessionToken) {
          await loadPublic();
          return;
        }
        const next = await getPlayGame(sessionToken, gameId);
        applyLoadedSnapshot(next, "player");
      } catch (loadError) {
        if (cancelled) {
          return;
        }
        if (
          loadError instanceof ApiError &&
          (loadError.status === 401 || loadError.status === 403)
        ) {
          try {
            await loadPublic();
            return;
          } catch (publicError) {
            if (cancelled) {
              return;
            }
            setError(resolveVisibleError(publicError, "failed to load game link"));
            setStatus("error");
            return;
          }
        }
        setError(resolveVisibleError(loadError, "failed to load game"));
        setStatus("error");
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [gameId, navigate]);

  useEffect(() => {
    if (!snapshot || snapshot.result || !accessMode) {
      return;
    }

    if (accessMode === "player" && snapshot.state === "USER_TURN") {
      return;
    }

    const sessionToken = getStoredSessionToken();
    if (accessMode === "player" && !sessionToken) {
      return;
    }

    const interval = window.setInterval(() => {
      if (syncInFlightRef.current) {
        return;
      }

      syncInFlightRef.current = true;
      const syncRequest =
        accessMode === "player" && sessionToken
          ? syncPlayGame(sessionToken, snapshot.game_id)
          : syncPublicPlayGame(snapshot.game_id);
      void syncRequest
        .then((next) => {
          setError(null);
          setSnapshot(next);
        })
        .catch((syncError) => {
          if (syncError instanceof ApiError && syncError.status === 409) {
            return;
          }
          setError(resolveVisibleError(syncError, "failed to sync game"));
        })
        .finally(() => {
          syncInFlightRef.current = false;
        });
    }, accessMode === "player" ? 250 : 1250);

    return () => {
      window.clearInterval(interval);
      syncInFlightRef.current = false;
    };
  }, [accessMode, snapshot]);

  useEffect(() => {
    const node = boardShellRef.current;
    if (!node) {
      return;
    }

    const updateBoardWidth = (width: number) => {
      const nextWidth = Math.max(260, Math.min(520, Math.floor(width - 18)));
      setBoardWidth(nextWidth);
    };

    updateBoardWidth(node.clientWidth);

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      updateBoardWidth(entry.contentRect.width);
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, [status]);

  useEffect(() => {
    if (!snapshot || snapshot.result) {
      return;
    }
    if (
      snapshot.state !== "USER_TURN" &&
      snapshot.state !== "MODEL_THINKING"
    ) {
      return;
    }

    const interval = window.setInterval(() => {
      setClockNowMs(Date.now());
    }, 250);

    return () => window.clearInterval(interval);
  }, [snapshot]);

  const { userClockMs, modelClockMs } = snapshot
    ? deriveLiveClocks(snapshot, clockNowMs)
    : { userClockMs: 0, modelClockMs: 0 };

  const timeoutSyncReason = useMemo(() => {
    if (!snapshot || snapshot.result) {
      return null;
    }
    if (snapshot.state === "USER_TURN" && snapshot.active_side === "user" && userClockMs <= 0) {
      return "user-timeout";
    }
    if (snapshot.state === "MODEL_THINKING" && snapshot.active_side === "model" && modelClockMs <= 0) {
      return "model-timeout";
    }
    return null;
  }, [modelClockMs, snapshot, userClockMs]);

  useEffect(() => {
    if (!timeoutSyncReason) {
      timeoutSyncKeyRef.current = null;
      return;
    }

    const sessionToken = getStoredSessionToken();
    if (!snapshot || syncInFlightRef.current) {
      return;
    }
    if (accessMode === "player" && !sessionToken) {
      return;
    }
    if (!accessMode) {
      return;
    }

    const syncKey = `${snapshot.game_id}:${snapshot.version}:${timeoutSyncReason}`;
    if (timeoutSyncKeyRef.current === syncKey) {
      return;
    }

    timeoutSyncKeyRef.current = syncKey;
    syncInFlightRef.current = true;
    const syncRequest =
      accessMode === "player" && sessionToken
        ? syncPlayGame(sessionToken, snapshot.game_id)
        : syncPublicPlayGame(snapshot.game_id);
    void syncRequest
      .then((next) => {
        setError(null);
        setSnapshot(next);
      })
      .catch((syncError) => {
        timeoutSyncKeyRef.current = null;
        setError(resolveVisibleError(syncError, "failed to sync game"));
      })
      .finally(() => {
        syncInFlightRef.current = false;
      });
  }, [accessMode, snapshot, timeoutSyncReason]);

  const board = useMemo(() => (snapshot ? buildBoard(snapshot) : null), [snapshot]);
  const activeReviewPly = reviewPly;
  const displayPly = snapshot ? activeReviewPly ?? snapshot.moves.length : 0;
  const displayBoard = useMemo(
    () => (snapshot ? buildBoard(snapshot, displayPly) : null),
    [displayPly, snapshot],
  );
  const currentFen = displayBoard?.fen() ?? null;
  const displayHistory = useMemo(
    () => (displayBoard ? (displayBoard.history({ verbose: true }) as Move[]) : []),
    [displayBoard],
  );
  const displayMoves = useMemo(
    () => displayHistory.map(moveToUci),
    [displayHistory],
  );
  const lastMove = useMemo(() => {
    if (!displayBoard) {
      return null;
    }
    return displayHistory.at(-1) ?? null;
  }, [displayBoard, displayHistory]);

  const orientation = snapshot?.user_color === "black" ? "black" : "white";
  const gameEnd = snapshot ? resolveGameEnd(snapshot) : null;
  const interactionLocked = Boolean(timeoutSyncReason);
  const isPlayerMode = accessMode === "player";
  const isPublicMode = accessMode === "public";
  const roomMode: SessionRoomMode =
    isPublicMode && snapshot?.result ? "replay" : isPublicMode ? "spectator" : "player";
  const isReviewing = activeReviewPly !== null;
  const canReviewMoves = Boolean(snapshot && snapshot.moves.length > 0);
  const canPlayMove =
    isPlayerMode &&
    !snapshot?.result &&
    snapshot?.state === "USER_TURN" &&
    !interactionLocked &&
    !isReviewing;
  const opponent = snapshot ? resolveOpponentPresentation(snapshot.model_id) : null;
  const sessionStatus = snapshot && opponent
    ? resolveSessionStatus(snapshot, opponent.title, interactionLocked, Boolean(error), roomMode)
    : null;
  const isReviewAnalysisMode = Boolean(snapshot?.result && currentFen && displayBoard);
  const {
    enabled: engineEnabled,
    setEnabled: setEngineEnabled,
    status: engineStatus,
    evaluation: engineEvaluation,
  } = useStockfishEvaluation({
    fen: currentFen,
    active: isReviewAnalysisMode,
    depth: 14,
    multiPv: 5,
  });

  useEffect(() => {
    setExpandedPv(null);
  }, [currentFen, engineEnabled]);

  useEffect(() => {
    if (accessMode !== "player") {
      setGameEndModal(null);
      return;
    }
    if (!snapshot || !gameEnd || !snapshot.result) {
      if (!snapshot?.result) {
        setGameEndModal(null);
        setGameEndVersion(null);
      }
      return;
    }
    if (gameEndVersion === snapshot.version) {
      return;
    }
    setGameEndModal(gameEnd);
    setGameEndVersion(snapshot.version);
  }, [accessMode, gameEnd, gameEndVersion, snapshot]);

  useEffect(() => {
    if (!snapshot?.result) {
      setCopyState("idle");
      setCopyError(null);
      setRematchState("idle");
      setRematchError(null);
    }
  }, [snapshot?.game_id, snapshot?.result]);

  useEffect(() => {
    if (!snapshot?.result) {
      return;
    }
    setIsResignConfirmOpen(false);
    setIsResigning(false);
    setResignError(null);
    setReviewPly(null);
  }, [snapshot?.result]);

  useEffect(() => {
    setReviewPly(null);
    setSelectedSquare(null);
    setLegalTargets([]);
    dragSourceSquareRef.current = null;

    const log = moveLogRef.current;
    if (!log) {
      return;
    }
    log.scrollTop = log.scrollHeight;
  }, [snapshot?.moves.length]);

  const customSquareStyles = useMemo(() => {
    const styles: Record<string, CSSProperties> = {};

    if (!isReviewing && selectedSquare) {
      styles[selectedSquare] = {
        boxShadow: lichessBoardPalette.selected,
      };
    }
    if (!isReviewing) {
      for (const square of legalTargets) {
        styles[square] = {
          background: lichessBoardPalette.legalTarget,
        };
      }
    }
    if (lastMove) {
      styles[lastMove.from] = {
        ...(styles[lastMove.from] ?? {}),
        backgroundColor: lichessBoardPalette.lastMove,
      };
      styles[lastMove.to] = {
        ...(styles[lastMove.to] ?? {}),
        backgroundColor: lichessBoardPalette.lastMove,
      };
    }
    return styles;
  }, [isReviewing, lastMove, legalTargets, selectedSquare]);

  const selectSquare = useCallback((square: Square) => {
    if (!board || !snapshot || !canPlayMove) {
      return;
    }

    const piece = board.get(square);
    const currentTurnColor = snapshot.user_color === "white" ? "w" : "b";
    if (!piece || piece.color !== currentTurnColor) {
      setSelectedSquare(null);
      setLegalTargets([]);
      return;
    }

    const moves = board.moves({ square, verbose: true });
    setSelectedSquare(square);
    setLegalTargets(moves.map((move) => move.to));
  }, [board, canPlayMove, snapshot]);

  const submitMove = useCallback(async (moveUci: string) => {
    const sessionToken = getStoredSessionToken();
    if (!sessionToken || !snapshot) {
      navigate("/login");
      return false;
    }

    try {
      const next = await submitPlayMove(sessionToken, snapshot.game_id, moveUci, snapshot.version);
      setSnapshot(next);
      setError(null);
      setSelectedSquare(null);
      setLegalTargets([]);
      dragSourceSquareRef.current = null;
      return true;
    } catch (moveError) {
      dragSourceSquareRef.current = null;
      setSelectedSquare(null);
      setLegalTargets([]);
      setError(resolveVisibleError(moveError, "failed to submit move"));
      if (moveError instanceof ApiError && moveError.status === 422) {
        try {
          const refreshed = await getPlayGame(sessionToken, snapshot.game_id);
          setSnapshot(refreshed);
        } catch {
          // Keep the original illegal-move error visible if the refresh fails.
        }
      }
      return false;
    }
  }, [navigate, snapshot]);

  const onSquareClick = useCallback((square: Square) => {
    if (!snapshot || !board || !canPlayMove) {
      return;
    }
    if (selectedSquare) {
      if (selectedSquare === square) {
        dragSourceSquareRef.current = null;
        setSelectedSquare(null);
        setLegalTargets([]);
        return;
      }
      if (legalTargets.includes(square)) {
        const moveUci = resolveUciMove(board, selectedSquare, square);
        if (!moveUci) {
          setError(`illegal interaction payload: ${selectedSquare}${square}`);
          setSelectedSquare(null);
          setLegalTargets([]);
          return;
        }
        void submitMove(moveUci);
        return;
      }
    }
    selectSquare(square);
  }, [board, canPlayMove, legalTargets, selectedSquare, selectSquare, snapshot, submitMove]);

  const onPieceDrop = useCallback((sourceSquare: Square, targetSquare?: Square) => {
    if (!targetSquare || !snapshot || !board || !canPlayMove) {
      dragSourceSquareRef.current = null;
      return false;
    }

    const actualSource = dragSourceSquareRef.current ?? sourceSquare;
    const moveUci = resolveUciMove(board, actualSource, targetSquare);

    if (!moveUci) {
      dragSourceSquareRef.current = null;
      setSelectedSquare(null);
      setLegalTargets([]);
      setError(`illegal interaction payload: ${actualSource}${targetSquare}`);
      return false;
    }

    void submitMove(moveUci);
    return true;
  }, [board, canPlayMove, snapshot, submitMove]);

  const onPieceDragBegin = useCallback((_piece: string, sourceSquare: Square) => {
    if (!canPlayMove) {
      return;
    }
    dragSourceSquareRef.current = sourceSquare;
    selectSquare(sourceSquare);
  }, [canPlayMove, selectSquare]);

  const onPieceDragEnd = useCallback(() => {
    dragSourceSquareRef.current = null;
  }, []);

  const openResignConfirm = () => {
    if (!isPlayerMode) {
      return;
    }
    setResignError(null);
    setIsResignConfirmOpen((current) => !current);
  };

  const closeResignConfirm = () => {
    if (isResigning) {
      return;
    }
    setResignError(null);
    setIsResignConfirmOpen(false);
  };

  const clearReviewState = useCallback(() => {
    setReviewPly(null);
    setSelectedSquare(null);
    setLegalTargets([]);
    dragSourceSquareRef.current = null;
  }, []);

  const scrollMoveLogToLatest = useCallback(() => {
    window.requestAnimationFrame(() => {
      const log = moveLogRef.current;
      if (log) {
        log.scrollTop = log.scrollHeight;
      }
    });
  }, []);

  const openReviewPly = useCallback((ply: number) => {
    if (!canReviewMoves || !snapshot) {
      return;
    }
    const nextReviewPly = normalizeReviewPly(ply, snapshot.moves.length);
    if (nextReviewPly === null) {
      clearReviewState();
      scrollMoveLogToLatest();
      return;
    }
    setReviewPly(nextReviewPly);
    setSelectedSquare(null);
    setLegalTargets([]);
    dragSourceSquareRef.current = null;
  }, [canReviewMoves, clearReviewState, scrollMoveLogToLatest, snapshot]);

  const returnToLatestPosition = useCallback(() => {
    clearReviewState();
    scrollMoveLogToLatest();
  }, [clearReviewState, scrollMoveLogToLatest]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!snapshot || !canReviewMoves || isKeyboardNavigationBlocked(event.target)) {
        return;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        openReviewPly(0);
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        openReviewPly(Math.max(0, displayPly - 1));
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        openReviewPly(Math.min(snapshot.moves.length, displayPly + 1));
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        returnToLatestPosition();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [canReviewMoves, displayPly, openReviewPly, returnToLatestPosition, snapshot]);

  const onResign = async () => {
    if (!isPlayerMode) {
      return;
    }
    const sessionToken = getStoredSessionToken();
    if (!sessionToken || !snapshot) {
      navigate("/login");
      return;
    }
    setIsResigning(true);
    setResignError(null);
    try {
      const next = await resignPlayGame(sessionToken, snapshot.game_id, snapshot.version);
      setSnapshot(next);
      setIsResignConfirmOpen(false);
    } catch (resignError) {
      const detail = resignError instanceof Error ? resignError.message : "failed to resign";
      setResignError(detail);
      setError(detail);
    } finally {
      setIsResigning(false);
    }
  };

  const onReturnToPlay = () => {
    navigate("/play");
  };

  const onRematch = async () => {
    if (!isPlayerMode) {
      return;
    }
    const sessionToken = getStoredSessionToken();
    if (!sessionToken || !snapshot) {
      navigate("/login");
      return;
    }

    setRematchState("starting");
    setRematchError(null);

    try {
      const created = await createPlayGame(sessionToken, {
        modelId: snapshot.model_id,
        elo: snapshot.elo,
        userColor: snapshot.user_color === "black" ? "black" : "white",
        hardness: snapshot.hardness ?? temperatureToHardness(snapshot.temperature),
        engineVersion: snapshot.engine_version ?? "catie_1_0",
        minutes: snapshot.time_control.minutes,
        incrementSeconds: snapshot.time_control.increment_seconds,
      });
      navigate(`/play/session/${created.game_id}`);
    } catch (startError) {
      if (startError instanceof ApiError && startError.status === 401) {
        navigate("/login");
        return;
      }
      if (
        startError instanceof ApiError &&
        startError.status === 409 &&
        startError.message.startsWith("active game exists:")
      ) {
        try {
          const current = await getActivePlayGame(sessionToken);
          if (current) {
            navigate(`/play/session/${current.game_id}`);
            return;
          }
        } catch {
          // Keep the visible rematch error when the active game lookup fails.
        }
      }
      setRematchState("error");
      setRematchError(resolveVisibleError(startError, "failed to start rematch"));
      return;
    }

    setRematchState("idle");
  };

  const onCopyPgn = async () => {
    if (!isPlayerMode) {
      return;
    }
    const sessionToken = getStoredSessionToken();
    if (!sessionToken || !snapshot) {
      navigate("/login");
      return;
    }

    setCopyState("copying");
    setCopyError(null);
    try {
      const pgn = await getPlayGamePgn(sessionToken, snapshot.game_id);
      if (!navigator.clipboard) {
        throw new Error("clipboard is not available in this browser");
      }
      await navigator.clipboard.writeText(pgn);
      setCopyState("copied");
    } catch (copyPgnError) {
      setCopyState("error");
      setCopyError(copyPgnError instanceof Error ? copyPgnError.message : "failed to copy PGN");
    }
  };

  if (status === "loading" || !snapshot || !board || !displayBoard) {
    return (
      <div className="page-stack">
        <section className="session-room">
          <LoadingBlock
            title="Establishing game room"
            detail="Syncing room authority, board state, and model channel."
            eyebrow="Play runtime"
            etaSeconds={6}
            mode="play"
          />
        </section>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="page-stack">
        <section className="session-room">
          <p className="session-room__status">{error ?? "Failed to load game room."}</p>
        </section>
      </div>
    );
  }

  const topClock =
    snapshot.user_color === "white" ? modelClockMs : userClockMs;
  const bottomClock =
    snapshot.user_color === "white" ? userClockMs : modelClockMs;
  const userDisplayName = snapshot.user_username ?? (isPlayerMode ? getStoredUsername() : null) ?? "Player";
  const playerSeatLabel = isPlayerMode ? "You" : userDisplayName;
  const modelDisplayName = opponent?.title ?? snapshot.model_id;
  const topClockLabel = snapshot.user_color === "white" ? modelDisplayName : playerSeatLabel;
  const bottomClockLabel = snapshot.user_color === "white" ? playerSeatLabel : modelDisplayName;
  const topClockActive =
    !snapshot.result && snapshot.active_side === (snapshot.user_color === "white" ? "model" : "user");
  const bottomClockActive =
    !snapshot.result && snapshot.active_side === (snapshot.user_color === "white" ? "user" : "model");
  const history = board.history({ verbose: true });
  const movePairs = [];

  for (let index = 0; index < history.length; index += 2) {
    movePairs.push({
      moveNumber: Math.floor(index / 2) + 1,
      white: history[index] ?? null,
      black: history[index + 1] ?? null,
      whitePly: index + 1,
      blackPly: index + 2,
    });
  }
  const ownerColorLabel = snapshot.user_color === "white" ? "White" : "Black";
  const roomEyebrow =
    roomMode === "replay" ? "Replay" : roomMode === "spectator" ? "Spectating" : "Opponent";
  const reviewPillClass = isReviewing
    ? "session-review-pill session-review-pill--review"
    : snapshot.result
      ? "session-review-pill session-review-pill--final"
      : "session-review-pill session-review-pill--live";
  const reviewPillLabel = isReviewing ? `View ${displayPly}` : snapshot.result ? "Final" : "Live";
  const activeMovePly = displayPly;
  const currentEvaluation =
    engineEvaluation?.fen === currentFen ? engineEvaluation : null;
  const enginePvs = currentEvaluation?.pvs ?? [];
  const primaryPv = enginePvs[0];
  const engineScoreLabel = !engineEnabled
    ? "OFF"
    : primaryPv && displayBoard
      ? formatStockfishScore(primaryPv, displayBoard.turn())
      : engineStatus === "error" || engineStatus === "unsupported"
        ? "ERR"
        : "...";
  const visibleEngineLineCount = showAllEngineLines ? 5 : 3;
  const engineLines = engineEnabled
    ? Array.from({ length: visibleEngineLineCount }, (_, index) => {
        const pv = enginePvs[index] as StockfishPv | undefined;
        return {
          rank: index + 1,
          eval: pv && displayBoard ? formatStockfishScore(pv, displayBoard.turn()) : "...",
          variation: pv && currentFen ? formatStockfishPvSan(currentFen, pv.pv) : "...",
          hasPv: Boolean(pv),
        };
      })
    : [];
  const reviewWhiteName =
    snapshot.user_color === "white" ? userDisplayName : modelDisplayName;
  const reviewBlackName =
    snapshot.user_color === "black" ? userDisplayName : modelDisplayName;
  const reviewCreatedLabel = formatOptionalReviewDate(snapshot.created_at);

  return (
    <div className="page-stack">
      <section className="session-room">
        {isReviewAnalysisMode ? (
          <aside className="session-rail session-rail--status session-analysis-rail">
            <div className="session-review-dossier" aria-label="Game dossier">
              <div className="session-review-dossier__top">
                <p className="session-rail__eyebrow">Review</p>
                <div className="session-review-dossier__meta">
                  <span>{formatTimeControlLabel(snapshot)}</span>
                  {reviewCreatedLabel ? (
                    <time dateTime={snapshot.created_at ?? undefined}>{reviewCreatedLabel}</time>
                  ) : null}
                </div>
              </div>
              <div className="session-review-dossier__players">
                <div className="session-review-dossier__player">
                  <span
                    className="session-review-dossier__stone session-review-dossier__stone--white"
                    aria-label="White"
                    role="img"
                  />
                  <strong>{reviewWhiteName}</strong>
                </div>
                <div className="session-review-dossier__player">
                  <span
                    className="session-review-dossier__stone session-review-dossier__stone--black"
                    aria-label="Black"
                    role="img"
                  />
                  <strong>{reviewBlackName}</strong>
                </div>
              </div>
            </div>

            <StockfishAnalysisPanel
              enabled={engineEnabled}
              status={engineStatus}
              scoreLabel={engineScoreLabel}
              lines={engineLines}
              expandedLine={expandedPv}
              showAllLines={showAllEngineLines}
              onToggleEnabled={() => setEngineEnabled((current) => !current)}
              onToggleLine={(rank) =>
                setExpandedPv((current) => (current === rank ? null : rank))
              }
              onToggleShowAllLines={() => setShowAllEngineLines((current) => !current)}
            />
            <AnalysisModelChoicePanel
              moves={displayMoves}
              fen={snapshot.initial_fen ? currentFen : null}
              positionKey={currentFen ?? `${snapshot.game_id}:${displayPly}`}
            />
          </aside>
        ) : (
        <aside className="session-rail session-rail--status">
          <div className="session-identity">
            <p className="session-rail__eyebrow">{roomEyebrow}</p>
            <h1>{opponent?.title ?? snapshot.model_id}</h1>
          </div>

          <div className="session-token-strip">
            <span className="session-chip">
              {playerSeatLabel}: {ownerColorLabel}
            </span>
            <span className="session-chip">Clock: {formatTimeControlLabel(snapshot)}</span>
          </div>

          {sessionStatus ? (
            <div className={`session-state-card session-state-card--${sessionStatus.tone}`}>
              <p className="session-state-card__label">{sessionStatus.label}</p>
              {sessionStatus.detail ? (
                <p className="session-state-card__detail">{sessionStatus.detail}</p>
              ) : null}
              {sessionStatus.tone === "thinking" || sessionStatus.tone === "incoming" ? (
                <div className="session-state-card__meter" aria-hidden="true">
                  <span />
                </div>
              ) : null}
              {sessionStatus.note ? (
                <p className="session-state-card__note">{sessionStatus.note}</p>
              ) : null}
            </div>
          ) : null}

          {isPlayerMode ? (
            <div className="session-rail__actions">
              <button
                className="button-link button-link--secondary"
                type="button"
                onClick={openResignConfirm}
                disabled={Boolean(snapshot.result) || interactionLocked}
              >
                Resign
              </button>
              {isResignConfirmOpen ? (
                <ResignConfirm
                  onCancel={closeResignConfirm}
                  onConfirm={onResign}
                  isSubmitting={isResigning}
                  error={resignError}
                />
              ) : null}
            </div>
          ) : (
            <div className="session-public-card">
              <span>{roomMode === "replay" ? "Archive link" : "Game link"}</span>
              <strong>{roomMode === "replay" ? "Full replay" : "Watch only"}</strong>
            </div>
          )}
        </aside>
        )}

        <div className="session-board-column">
          <div className={`session-clock session-clock--top ${topClockActive ? "session-clock--active" : ""}`}>
            <span className="session-clock__side">{topClockLabel}</span>
            <span className="session-clock__time">{formatClock(topClock)}</span>
          </div>
          <div ref={boardShellRef} className="session-board-shell">
            <ChessSurface
              id={`play-session-${snapshot.game_id}`}
              boardWidth={boardWidth}
              position={displayBoard.fen()}
              orientation={orientation}
              arePiecesDraggable={canPlayMove}
              onPieceDrop={onPieceDrop}
              onPieceDragBegin={onPieceDragBegin}
              onPieceDragEnd={onPieceDragEnd}
              onSquareClick={onSquareClick}
              customSquareStyles={customSquareStyles}
              darkSquareColor={lichessBoardPalette.dark}
              lightSquareColor={lichessBoardPalette.light}
            />
          </div>
          <div className={`session-clock session-clock--bottom ${bottomClockActive ? "session-clock--active" : ""}`}>
            <span className="session-clock__side">{bottomClockLabel}</span>
            <span className="session-clock__time">{formatClock(bottomClock)}</span>
          </div>
        </div>

        <aside className="session-rail session-rail--moves">
          <div className="session-rail__head">
            <p className="session-rail__eyebrow">Moves</p>
            <div className="session-review-control">
              <span className={reviewPillClass}>{reviewPillLabel}</span>
              {isReviewing ? (
                <button
                  className="session-review-latest"
                  type="button"
                  onClick={returnToLatestPosition}
                >
                  Latest
                </button>
              ) : (
                <span className="session-rail__meta">{history.length} ply</span>
              )}
            </div>
          </div>
          <div ref={moveLogRef} className="move-log">
            {movePairs.length > 0 ? (
              movePairs.map((pair) => (
                <div
                  key={pair.moveNumber}
                  className={
                    activeMovePly === pair.whitePly || activeMovePly === pair.blackPly
                      ? "move-log__row move-log__row--active"
                      : "move-log__row"
                  }
                >
                  <span className="move-log__index">{pair.moveNumber}.</span>
                  <span className="move-log__move">
                    {pair.white ? (
                      <button
                        className={
                          activeMovePly === pair.whitePly
                            ? "move-log__move-button move-log__move-button--active"
                            : "move-log__move-button"
                        }
                        type="button"
                        onClick={() => openReviewPly(pair.whitePly)}
                        disabled={!canReviewMoves}
                        aria-label={`View position after ${pair.moveNumber}. ${pair.white.san}`}
                      >
                        {pair.white.san}
                      </button>
                    ) : null}
                  </span>
                  <span className="move-log__move">
                    {pair.black ? (
                      <button
                        className={
                          activeMovePly === pair.blackPly
                            ? "move-log__move-button move-log__move-button--active"
                            : "move-log__move-button"
                        }
                        type="button"
                        onClick={() => openReviewPly(pair.blackPly)}
                        disabled={!canReviewMoves}
                        aria-label={`View position after ${pair.moveNumber}... ${pair.black.san}`}
                      >
                        {pair.black.san}
                      </button>
                    ) : (
                      <span className="move-log__move-placeholder" aria-hidden="true" />
                    )}
                  </span>
                </div>
              ))
            ) : (
              <p className="move-log__empty">No moves yet.</p>
            )}
          </div>
        </aside>
      </section>
      {gameEndModal && isPlayerMode ? (
        <GameEndModal
          outcome={gameEndModal}
          onReturn={onReturnToPlay}
          onCopyPgn={onCopyPgn}
          onRematch={onRematch}
          copyState={copyState}
          rematchState={rematchState}
          copyError={copyError}
          rematchError={rematchError}
          onDismiss={() => setGameEndModal(null)}
        />
      ) : null}
    </div>
  );
}
